/**
 * Участники и приглашения.
 *
 * Роли берутся из контракта, а не сочиняются экраном: их четыре, и у каждой в
 * `access.ts` записано, что она достаёт из API. Здесь показывается **следствие**
 * — одна строка про то, что человек сможет делать. Список «может / не может» в
 * двух местах разъедется на первой же правке прав.
 *
 * Два ограничения приходят от базы и проговариваются словами до нажатия, а не
 * после отказа:
 *
 * * **клиент агентства живёт ровно в одном пространстве** — без него роль не
 *   имеет смысла, поэтому выбор пространства обязателен и появляется вместе с
 *   ролью;
 * * **последнего владельца нельзя ни разжаловать, ни убрать** — организация без
 *   владельца теряет доступ к оплате.
 *
 * Приглашение, смена роли и отзыв доступа требуют подтверждения на действии
 * (docs/API.md §2): это ровно те действия, ради которых угоняют сессию.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';

import { Field, Panel } from '../ui/form';
import { Dialog } from '../ui/Dialog';
import { Empty, ErrorNotice, Skeleton } from '../ui/state';
import { StepUpCancelled, useConfirmed } from '../ui/StepUp';
import { useScope } from '../scope';
import { useAccess, useSession } from '../session';
import { ROLE_NAMES, ROLE_SUMMARY, type Role } from '../api/access';
import { idempotencyKey } from '../api/client';
import {
  cancelOwnershipTransfer,
  getOwnershipTransfer,
  inviteMember,
  listInvitations,
  listMembers,
  removeMember,
  resendInvitation,
  revokeInvitation,
  startOwnershipTransfer,
  updateMember,
  type Invitation,
  type Member,
  type OwnershipTransfer,
} from '../api/team';
import { dateProse, daysLeft, momentProse } from '@shared/src/format';

/**
 * Роли, которые выдаются приглашением и сменой роли.
 *
 * Владельца здесь нет намеренно: владение не «выдаётся ролью», а **передаётся**
 * отдельным потоком с подтверждением обеими сторонами
 * (`POST /organizations/current/ownership-transfer`). Пункт «Владелец» в
 * выпадающем списке ролей означал бы, что доступ к деньгам организации отдаётся
 * одним кликом и без ведома того, кому его отдают.
 */
const INVITABLE: Role[] = ['analyst', 'viewer', 'agency_client'];

type Load =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      members: Member[];
      invitations: Invitation[];
      transfer: OwnershipTransfer | null;
    }
  | { kind: 'failed'; error: unknown };

export function Members() {
  const session = useSession();
  const access = useAccess();
  const scope = useScope();
  const confirmed = useConfirmed();

  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);

  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('analyst');
  const [workspaceId, setWorkspaceId] = useState('');
  const [removing, setRemoving] = useState<Member | null>(null);
  const [handing, setHanding] = useState(false);
  const [heir, setHeir] = useState('');
  const [heirConfirmed, setHeirConfirmed] = useState(false);

  // Матрица, а не роль на глаз: передачу владения контракт отдаёт владельцу, и
  // спрашивать её у аналитика значит показать ему отказ вместо экрана.
  const mayHandOver = access.reaches('POST', '/organizations/current/ownership-transfer');

  useEffect(() => {
    const abort = new AbortController();
    Promise.all([
      listMembers(abort.signal),
      listInvitations(abort.signal),
      // Передача — состояние организации, а не участника, но живёт она здесь:
      // выбирают преемника из этого же списка. Роли без права её и не видят.
      mayHandOver ? getOwnershipTransfer(abort.signal) : Promise.resolve(null),
    ])
      .then(([members, invitations, transfer]) =>
        setLoad({
          kind: 'ready',
          members: members.items,
          invitations: invitations.items,
          transfer,
        }),
      )
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setLoad({ kind: 'failed', error });
      });
    return () => abort.abort();
    // `mayHandOver` выводится из сессии и за время жизни экрана не меняется;
    // в зависимостях он давал бы лишний повтор загрузки на каждой перерисовке.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  const act = useCallback(
    async (key: string, run: () => Promise<string>) => {
      setBusy(key);
      setFailure(null);
      setNote(null);
      try {
        setNote(await run());
        reload();
      } catch (error) {
        if (error instanceof StepUpCancelled) return;
        setFailure(error);
      } finally {
        setBusy(null);
      }
    },
    [reload],
  );

  const workspaces = scope.state.kind === 'ready' ? scope.state.workspaces : [];

  async function submitInvite(event: FormEvent) {
    event.preventDefault();
    const target = role === 'agency_client' ? workspaceId || workspaces[0]?.id : null;
    setInviting(false);
    await act('invite', async () => {
      // Ключ создаётся один раз на попытку: повтор после подтверждения обязан
      // уйти с тем же ключом, иначе на почту придёт второе письмо.
      const key = idempotencyKey();
      await confirmed('members.invite', (token) =>
        inviteMember(
          { email: email.trim(), role, ...(target ? { workspace_id: target } : {}) },
          key,
          token,
        ),
      );
      setEmail('');
      return `Приглашение отправлено на ${email.trim()}. Ссылка привязана к этому адресу и живёт семь дней.`;
    });
  }

  async function submitHandOver() {
    const target = heir;
    setHanding(false);
    setHeirConfirmed(false);
    if (!target) return;
    await act('ownership', async () => {
      const key = idempotencyKey();
      await confirmed('ownership.transfer', (token) =>
        startOwnershipTransfer(target, key, token),
      );
      setHeir('');
      return `Передача начата: ${target} получил письмо. Пока он не подтвердит, владелец организации — по-прежнему вы.`;
    });
  }

  const head = (
    <div className="screen-head">
      <h1>Участники</h1>
      <button
        className="btn btn-primary btn-sm"
        type="button"
        onClick={() => setInviting(true)}
      >
        Пригласить участника
      </button>
    </div>
  );

  if (load.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={2} />
      </>
    );
  }
  if (load.kind === 'failed') {
    return (
      <>
        {head}
        <ErrorNotice
          error={load.error}
          action={
            <button className="btn btn-ghost btn-sm" type="button" onClick={reload}>
              Повторить
            </button>
          }
        />
      </>
    );
  }

  const { members, invitations, transfer } = load;
  const owners = members.filter((m) => m.role === 'owner').length;

  /**
   * Кому можно передать владение: любому участнику, кроме себя.
   *
   * Совладельцы из списка не убраны намеренно. Передача — это не «выдать роль»,
   * а «снять её с себя»: у организации с двумя владельцами один может уйти,
   * оставив второго единственным, и это ровно тот случай, ради которого поток и
   * нужен.
   */
  const heirs = members.filter((member) => member.user_id !== session.user.id);
  const pending = transfer && transfer.state === 'pending' ? transfer : null;

  return (
    <>
      {head}

      {failure ? <ErrorNotice error={failure} /> : null}
      {note ? (
        <p className="notice ok" role="status">
          <b>Готово</b>
          {note}
        </p>
      ) : null}

      <Panel
        title="В организации"
        lead="Число сотрудников входит в тариф всегда — за участников мы не берём денег."
      >
        <div className="tbl-wrap">
          <table className="mini">
            <caption className="stamp">
              Участники {session.organization?.name ?? 'организации'} · всего{' '}
              {members.length}
            </caption>
            <thead>
              <tr>
                <th scope="col">Кто</th>
                <th scope="col">Роль</th>
                <th scope="col">Последний вход</th>
                <th scope="col">
                  <span className="visually-hidden">Действия</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => {
                /**
                 * Владельцу роль не меняют этим списком — **любому**, а не
                 * только единственному.
                 *
                 * Раньше особым случаем был лишь последний владелец, а при двух
                 * и более совладельцах каждому рисовался `select` с
                 * `value="owner"`, которого нет в списке опций (владение
                 * передаётся отдельным потоком, поэтому `owner` в `INVITABLE`
                 * и не входит). Браузер на несовпадающем `value` показывает
                 * пустое поле или первую опцию — «Аналитик», — а подпись под
                 * ним честно писала «Полный доступ, включая оплату и
                 * участников». Экран противоречил сам себе, и один щелчок по
                 * такому списку молча отправлял `PATCH` с ролью аналитика,
                 * разжалуя совладельца.
                 */
                const isOwner = member.role === 'owner';
                const lastOwner = isOwner && owners === 1;
                const self = member.user_id === session.user.id;
                return (
                  <tr key={member.id}>
                    <td>
                      <b>{member.full_name || member.email}</b>
                      <span className="stamp">
                        {member.email}
                        {self ? ' · это вы' : ''}
                      </span>
                    </td>
                    <td>
                      {isOwner ? (
                        <>
                          <b>{ROLE_NAMES.owner}</b>
                          <span className="stamp">
                            {lastOwner
                              ? 'единственный владелец — роль сменить нельзя'
                              : 'владение передают отдельно, а не сменой роли'}
                          </span>
                        </>
                      ) : (
                        <>
                          <label
                            className="visually-hidden"
                            htmlFor={`role-${member.id}`}
                          >
                            Роль участника {member.full_name || member.email}
                          </label>
                          <select
                            id={`role-${member.id}`}
                            className="inline-select"
                            value={member.role}
                            disabled={busy === `role-${member.id}`}
                            onChange={(event) => {
                              const next = event.target.value as Role;
                              void act(`role-${member.id}`, async () => {
                                await confirmed('members.update', (token) =>
                                  updateMember(
                                    member.id,
                                    {
                                      role: next,
                                      scope:
                                        next === 'agency_client'
                                          ? 'workspace'
                                          : 'organization',
                                      workspace_id:
                                        next === 'agency_client'
                                          ? (member.workspace_id ??
                                            workspaces[0]?.id ??
                                            null)
                                          : null,
                                    },
                                    token,
                                  ),
                                );
                                return `${member.full_name || member.email} теперь ${ROLE_NAMES[next].toLowerCase()}.`;
                              });
                            }}
                          >
                            {INVITABLE.map((option) => (
                              <option key={option} value={option}>
                                {ROLE_NAMES[option]}
                              </option>
                            ))}
                          </select>
                          <span className="stamp">
                            {ROLE_SUMMARY[member.role]}
                            {member.role === 'agency_client' && member.workspace_id
                              ? ` · ${workspaces.find((w) => w.id === member.workspace_id)?.name ?? 'пространство клиента'}`
                              : ''}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="tnum">
                      {member.last_login_at
                        ? momentProse(member.last_login_at)
                        : 'ещё не входил'}
                    </td>
                    <td className="right">
                      {lastOwner ? null : (
                        <button
                          className="btn btn-ghost btn-sm"
                          type="button"
                          onClick={() => setRemoving(member)}
                        >
                          Отозвать доступ
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel
        title="Приглашения"
        lead="Ссылка привязана к конкретному адресу, одноразовая и живёт семь дней: пересланное приглашение постороннего не пускает."
      >
        {invitations.length === 0 ? (
          <Empty eyebrow="Приглашения" title="Непринятых приглашений нет">
            Все приглашённые уже в организации. Новое приглашение уходит письмом и
            появится здесь до тех пор, пока его не примут.
          </Empty>
        ) : (
          <ul className="rows">
            {invitations.map((invitation) => (
              <li key={invitation.id}>
                <span>
                  <b>{invitation.email}</b>
                  <span className="stamp">
                    {ROLE_NAMES[invitation.role as Role]}
                    {invitation.created_at
                      ? ` · отправлено ${dateProse(invitation.created_at)}`
                      : ''}{' '}
                    · истекает {daysLeft(invitation.expires_at)}
                  </span>
                </span>
                <span className="row-actions">
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    disabled={busy === `resend-${invitation.id}`}
                    onClick={() =>
                      act(`resend-${invitation.id}`, async () => {
                        await resendInvitation(invitation.id, idempotencyKey());
                        return `Письмо ушло заново на ${invitation.email}. Прежняя ссылка погашена.`;
                      })
                    }
                  >
                    Выслать снова
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    disabled={busy === `revoke-${invitation.id}`}
                    onClick={() =>
                      act(`revoke-${invitation.id}`, async () => {
                        await revokeInvitation(invitation.id);
                        return 'Приглашение отозвано, ссылка больше не работает.';
                      })
                    }
                  >
                    Отозвать
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/* --- владение организацией --- */}
      {mayHandOver ? (
        <Panel
          title="Владение организацией"
          lead="Владелец один. Он платит, заводит и убирает участников и распоряжается данными организации — поэтому владение не выдаётся ролью, а передаётся, и только с согласия принимающего."
        >
          {pending ? (
            <>
              <p className="notice warn" role="status">
                <b>Передача начата</b>
                Ждём подтверждения от {pending.to_email}. Пока он не подтвердит, владелец
                — по-прежнему вы, и ничего не изменилось.
                {pending.expires_at
                  ? ` Приглашение истекает ${daysLeft(pending.expires_at)}.`
                  : ''}
              </p>
              <div className="row-actions">
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  disabled={busy === 'ownership-cancel'}
                  onClick={() =>
                    act('ownership-cancel', async () => {
                      await cancelOwnershipTransfer();
                      return 'Передача отменена. Владельцем организации остаётесь вы.';
                    })
                  }
                >
                  Отменить передачу
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="stamp">
                Сейчас владелец — {session.user.full_name || session.user.email}.
                {heirs.length === 0
                  ? ' Передавать владение некому: кроме вас в организации никого нет.'
                  : ''}
              </p>
              {heirs.length === 0 ? (
                <Empty eyebrow="Передача владения" title="Передать владение пока некому">
                  Владение передаётся участнику организации. Пригласите человека и
                  дождитесь, пока он примет приглашение, — после этого его можно будет
                  выбрать преемником.
                </Empty>
              ) : (
                <div className="row-actions">
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() => {
                      setHeir(heirs[0]?.email ?? '');
                      setHeirConfirmed(false);
                      setHanding(true);
                    }}
                  >
                    Передать владение
                  </button>
                </div>
              )}
            </>
          )}
        </Panel>
      ) : null}

      {/* --- приглашение --- */}
      <Dialog
        open={inviting}
        title="Пригласить участника"
        onClose={() => setInviting(false)}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => setInviting(false)}
            >
              Отмена
            </button>
            <button
              className="btn btn-primary btn-sm"
              type="submit"
              form="invite-form"
              disabled={busy === 'invite'}
            >
              Отправить приглашение
            </button>
          </>
        }
      >
        <form id="invite-form" onSubmit={submitInvite} noValidate>
          <Field
            id="invite-email"
            label="Рабочая почта"
            hint="Приглашение привязывается к этому адресу и другому не отдаётся"
          >
            {(props) => (
              <input
                {...props}
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>

          <div className="field">
            <label htmlFor="invite-role">Роль</label>
            <select
              id="invite-role"
              value={role}
              onChange={(event) => setRole(event.target.value as Role)}
            >
              {INVITABLE.map((option) => (
                <option key={option} value={option}>
                  {ROLE_NAMES[option]}
                </option>
              ))}
            </select>
            <span className="hint">{ROLE_SUMMARY[role]}</span>
          </div>

          {role === 'agency_client' ? (
            <div className="field">
              <label htmlFor="invite-workspace">Пространство клиента</label>
              <select
                id="invite-workspace"
                value={workspaceId || workspaces[0]?.id || ''}
                onChange={(event) => setWorkspaceId(event.target.value)}
              >
                {workspaces.map((workspace) => (
                  <option key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </option>
                ))}
              </select>
              <span className="hint">
                Клиент увидит только это пространство — ни других клиентов, ни ядра, ни
                настроек
              </span>
            </div>
          ) : null}
        </form>
      </Dialog>

      {/* --- отзыв доступа --- */}
      <Dialog
        open={removing !== null}
        title="Отозвать доступ"
        onClose={() => setRemoving(null)}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => setRemoving(null)}
            >
              Оставить доступ
            </button>
            <button
              className="btn btn-danger btn-sm"
              type="button"
              disabled={busy === 'remove'}
              onClick={() => {
                const member = removing;
                setRemoving(null);
                if (!member) return;
                void act('remove', async () => {
                  await confirmed('members.remove', (token) =>
                    removeMember(member.id, token),
                  );
                  return `${member.full_name || member.email} больше не имеет доступа. Всё, что он завёл, осталось организации.`;
                });
              }}
            >
              Отозвать доступ
            </button>
          </>
        }
      >
        <p className="dlg-lead">
          {removing?.full_name || removing?.email} потеряет доступ немедленно, вместе со
          всеми открытыми сессиями. Бренды, вопросы и отчёты, которые он завёл, остаются
          организации — удаляется членство, а не работа.
        </p>
      </Dialog>

      {/* --- передача владения --- */}
      <Dialog
        open={handing}
        title="Передать владение"
        onClose={() => {
          setHanding(false);
          setHeirConfirmed(false);
        }}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => {
                setHanding(false);
                setHeirConfirmed(false);
              }}
            >
              Оставить владение себе
            </button>
            {/* Кнопка называет действие и заперта до осознанной отметки:
                отменить передачу после подтверждения преемником вы уже не
                сможете — это его право, не ваше. */}
            <button
              className="btn btn-danger btn-sm"
              type="button"
              disabled={!heir || !heirConfirmed || busy === 'ownership'}
              onClick={() => void submitHandOver()}
            >
              Передать владение
            </button>
          </>
        }
      >
        <p className="dlg-lead">
          Владение получит участник, которого вы выберете, — но не сразу: сначала ему
          уйдёт письмо, и роли поменяются только когда он подтвердит. До тех пор владелец
          организации — вы, и передачу можно отменить.
        </p>

        <div className="field">
          <label htmlFor="heir">Кому передаём</label>
          <select
            id="heir"
            value={heir}
            onChange={(event) => setHeir(event.target.value)}
          >
            {heirs.map((member) => (
              <option key={member.id} value={member.email}>
                {member.full_name
                  ? `${member.full_name} · ${member.email}`
                  : member.email}
              </option>
            ))}
          </select>
          <span className="hint">
            Выбрать можно только участника организации — постороннего сначала приглашают
          </span>
        </div>

        <p className="notice warn" role="note">
          <b>Обратно вы это не отмените</b>
          После подтверждения преемником вы перестаёте быть владельцем: оплата, участники,
          тариф и удаление организации переходят к нему. Вернуть владение сможет только он
          — тем же самым способом. Ваш доступ к данным при этом остаётся: вы останетесь в
          организации, но с обычной ролью.
        </p>

        <label className="consent" htmlFor="heir-confirmed">
          <input
            id="heir-confirmed"
            type="checkbox"
            checked={heirConfirmed}
            onChange={(event) => setHeirConfirmed(event.target.checked)}
          />
          Я понимаю, что перестану быть владельцем и не смогу вернуть владение сам
        </label>
      </Dialog>
    </>
  );
}
